<template>
  <div>
    <div ref="referenceRef" class="inline-block" @click="presentPopover">
      <slot name="popoverReference"></slot>
    </div>
    <IonPopover
      v-if="hasBeenOpened"
      :is-open="isOpen"
      :event="presentEvent"
      :css-class="`${cssClass ?? ''} custom-ion-popover`"
      :side="side"
      :alignment="alignment"
      :size="size"
      @willPresent="onWillPresent"
      @didDismiss="onDidDismiss"
    >
      <div class="p-1">
        <slot name="popoverContent" :dismissPopover="dismissPopover"></slot>
      </div>
    </IonPopover>
  </div>
</template>

<script lang="ts">
import { IonPopover } from "@ionic/vue";
import type { PositionAlign, PositionSide } from "@ionic/core";
import { defineComponent, PropType, ref } from "vue";

export default defineComponent({
  name: "CustomIonPopover",
  components: { IonPopover },
  props: {
    cssClass: { type: String, default: undefined },
    disabled: { type: Boolean, default: false },
    side: { type: String as PropType<PositionSide>, default: "bottom" },
    alignment: { type: String as PropType<PositionAlign>, default: "center" },
    size: { type: String as PropType<"cover" | "auto">, default: "auto" },
  },
  emits: ["onOpen", "onClose"],
  setup(props, { emit }) {
    // Opened by is-open + event, NOT by ion-popover's `trigger`. `trigger` takes a DOM id that
    // Ionic resolves with document.getElementById at three moments it owns (connectedCallback,
    // componentDidLoad, and the trigger watcher). In a list that mounts and unmounts quickly —
    // e.g. question rows dropped the instant a recommendations response empties them — the
    // reference element is already gone by then, and Ionic logs a "trigger element ... was not
    // found in the DOM" warning per popover. Nothing here needs a document-wide id, so the id
    // goes away and the warning cannot happen. This also drops the module-global id counter and
    // works inside detached or teleported trees, where getElementById never resolves.
    const referenceRef = ref<HTMLElement | null>(null);
    const isOpen = ref(false);
    // The popover element itself is only created on the first open, so a screen full of
    // never-opened tooltips no longer pays for a live ion-popover per row.
    const hasBeenOpened = ref(false);
    const presentEvent = ref<CustomEvent | undefined>(undefined);

    function presentPopover(): void {
      if (props.disabled || referenceRef.value === null) {
        return;
      }
      // Ionic sizes and positions the popover against `event.detail.ionShadowTarget` before
      // falling back to `event.target`. Naming the reference wrapper keeps the anchor box
      // identical to what `trigger` used, instead of shrinking to whichever inner node was
      // clicked.
      presentEvent.value = new CustomEvent("click", {
        detail: { ionShadowTarget: referenceRef.value },
      });
      // Both flags flip in the same tick on the first open, so the element is created already
      // carrying is-open — ion-popover presents that from componentDidLoad. Flipping is-open a
      // tick later would instead rely on the isOpen watcher, which is not wired up yet while the
      // element is still loading. Every later open is a real false -> true change the watcher sees.
      hasBeenOpened.value = true;
      isOpen.value = true;
    }

    function onWillPresent(): void {
      emit("onOpen");
    }

    function onDidDismiss(): void {
      isOpen.value = false;
      // Held only for the enter/leave animations; releasing it after dismissal keeps the
      // wrapper from pinning the reference element once the row it belongs to is gone.
      presentEvent.value = undefined;
      emit("onClose");
    }

    function dismissPopover(): void {
      isOpen.value = false;
    }

    return {
      referenceRef,
      isOpen,
      hasBeenOpened,
      presentEvent,
      presentPopover,
      onWillPresent,
      onDidDismiss,
      dismissPopover,
    };
  },
});
</script>
<style lang="scss">
.custom-ion-popover {
  --width: auto;
}

ion-popover.tooltip-style {
  --background: var(--color-light);
  --box-shadow: var(--shadow-card);
  --backdrop-opacity: 0;
  --width: auto;
  --max-width: 16rem;
  &::part(arrow) {
    display: none;
  }
  &::part(content) {
    border: 1px solid var(--color-medium-light);
    border-radius: var(--control-radius);
  }
}
</style>
